import React, { useState, useEffect } from "react";
import { Accordion } from "react-bootstrap";
import { KeyResultsAccordionItem } from "./KeyResultsAccordionItem";
import "./key-results-accordion.scss";
import { useDispatch, useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import { MousePointer, PlusIcon } from "lucide-react";
import {
  LightText,
  PrimaryNormalText,
  SmallBoldText,
  SmallLightText,
  SmallNormalText,
} from "../../../../../utils/typographyUtils";
import StandardButton from "../../../../shared/formComponents/StandardButton/StandardButton";
import { ConditionalRender } from "../../../../shared/ConditionalRender/ConditionalRender";
export const KeyResultsAccordion = ({
  data,
  isViewMode,
  objectiveId,
  callBack,
  showAddKeyResultField,
  isPlanPublished,
  showOnHoverText,
  editMode,
  setShowAddKeyResultField,
  expandedKeyResult,
  disableOperations,
  isOwner,
  expandedObjective,
  closeAllAccordions,
  showActions,
}) => {
  const [showConfirmationButtons, setShowConfirmationButtons] = useState({
    show: false,
    type: "",
  });
  const [expandedItem, setExpandedItem] = useState(
    expandedKeyResult !== undefined ? expandedKeyResult : null
  );
  const [keyResultsData, setKeyResultsData] = useState(data);
  const isBIPLeader = useSelector((state) => state.authDetails.isBIPLeader);
  const [isExpanded, setIsExpanded] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const dispatch = useDispatch();
  const [showInitiativeInput, setShowInitiativeInput] = useState({
    show: false,
    id: "",
    type: "",
  });
  const [keyResult, setKeyResult] = useState("");
  const [showDeleteKeyResultPopup, setShowDeleteKeyResultPopup] =
    useState(false);
  useEffect(() => {
    if (closeAllAccordions) {
      setIsExpanded(false);
    }
  }, [closeAllAccordions]);

  const handleShowOnHoverText = () => {
    if (showOnHoverText) {
      return (
        <div className="bg-white rounded py-1 w-auto px-2 me-3">
          <SmallLightText>
            <MousePointer
              fill="#656565"
              className="me-2"
              height={16}
              width={16}
            />
            Hover over a Key Result to edit or delete
          </SmallLightText>
        </div>
      );
    }
    return null;
  };

  const renderEmptyState = () => {
    if (isViewMode) {
      return (
        <div className={"mt-2"}>
          <LightText>
            There were no key results added in this objective.
          </LightText>
        </div>
      );
    }

    if (isBIPLeader || isOwner) {
      return (
        <div className={"mt-2"}>
          <LightText>
            <PrimaryNormalText
              onClick={() => setShowAddKeyResultField(true)}
              classNames={"cursor-pointer text-decoration-underline"}
            >
              Click here
            </PrimaryNormalText>{" "}
            to add Key Result — a measurable outcome that indicates progress
            toward the objective.
          </LightText>
        </div>
      );
    } else {
      return (
        <div className={"mt-2"}>
          <LightText>Leader/Objective owner only can add Key Results</LightText>
        </div>
      );
    }
  };

  useEffect(() => {
    if (showAddKeyResultField) {
      const element = document.getElementById("add-key-result");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [showAddKeyResultField]);

  return (
    <div className="key-results-accordion-item border-bottom">
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className={`key-results-accordion-item-header cursor-pointer d-flex align-items-center ${
          isExpanded
            ? "px-3 py-2 key-results-accordion-item-header-active"
            : "p-3"
        } `}
      >
        <div className="d-flex align-items-center w-100">
          <img src={`/assets/images/key-results.svg`} className="me-2" />
          <SmallBoldText>
            Key Results{" "}
            <LightText classNames={"font-weight-normal"}>
              ({keyResultsData.length})
            </LightText>
          </SmallBoldText>
          <ConditionalRender condition={showActions && isExpanded}>
            <div>
              <StandardButton
                text="Add"
                color="btn-outline-brand-blue-new"
                className="py-1 ms-3"
                reversed
                icon={
                  <PlusIcon
                    viewBox="0 0 24 26"
                    size={16}
                    height={16}
                    width={16}
                  />
                }
                disabled={true}
              />
            </div>
          </ConditionalRender>
        </div>
        <div className="d-flex w-50 justify-content-end">
          <ConditionalRender condition={isExpanded}>
            {handleShowOnHoverText()}
          </ConditionalRender>
          <img
            className="cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
            src={`/assets/images/${
              isExpanded ? "up-arrow.svg" : "down-arrow.svg"
            }`}
          />
        </div>
      </div>
      <div
        className={`key-results-accordion-item-body px-3  ${
          isExpanded ? "open py-2" : "p-0"
        }`}
      >
        {keyResultsData.length === 0 && !showAddKeyResultField ? (
          <div className="text-center">{renderEmptyState()}</div>
        ) : (
          <div className="border rounded pt-3 pb-1">
            <div className="border-bottom">
              <div className="row px-3 pb-1">
                <div
                  className={`${isPlanPublished ? "col-6" : "col-7"} d-flex`}
                >
                  <SmallNormalText classNames={"me-4 key-result-text"}>
                    #
                  </SmallNormalText>
                  <SmallNormalText classNames={"key-result-text"}>
                    Metric
                  </SmallNormalText>
                </div>
                <div className={`col-4`}>
                  <SmallNormalText classNames={"key-result-text"}>
                    Target
                  </SmallNormalText>
                </div>
                <ConditionalRender condition={isPlanPublished}>
                  <div className="col-2">
                    <SmallNormalText classNames={"key-result-text"}>
                      Outcome
                    </SmallNormalText>
                  </div>
                </ConditionalRender>
              </div>
            </div>
            <div>
              {keyResultsData.map((item, index) => (
                <KeyResultsAccordionItem
                  key={item.keyResultId}
                  isLastItem={isPlanPublished && keyResultsData.length === 1}
                  isOwner={true}
                  isViewMode={isViewMode}
                  isExpanded={expandedItem === index}
                  number={index + 1}
                  onClick={() => {
                    if (expandedItem === index) {
                      setExpandedItem(null);
                    } else {
                      setExpandedItem(index);
                    }
                  }}
                  isPlanPublished={isPlanPublished}
                  disableOperations={disableOperations}
                  itemData={item}
                  editMode={editMode}
                  showConfirmationButtons={showConfirmationButtons}
                  setShowConfirmationButtons={setShowConfirmationButtons}
                  buttonDisabled={buttonDisabled}
                  showInitiativeInput={showInitiativeInput}
                  setShowDeleteKeyResultPopup={setShowDeleteKeyResultPopup}
                  setShowInitiativeInput={setShowInitiativeInput}
                  expandedObjective={expandedObjective}
                  showActions={showActions}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
